import {
  EvaluatedResults,
  IAMUserState,
  JsonSchema,
  RowData,
  RowDataValueType,
} from "../../../types";
import { ROW_DATA_KEYS, SCHEMA_TITLES } from "../constants";
import {
  getFormIdOrProvisionIdLabel,
  getLabelForEnumValue,
} from "./evaluationHelpers";
import { getSelectedGroupIdOption } from "./group-id";
import { formatFieldValueToString } from "./tableHelpers";

export type FieldValueContext = {
  fieldName: string;
  fieldValue: RowDataValueType;
  rowData: RowData;
  setupName: string;
  schema: JsonSchema;
  iamUserState: IAMUserState;
  evaluatedResults: EvaluatedResults;
};

const getAccountsReceivableScheduleCodeDisplayValue = ({
  fieldName,
  fieldValue,
  rowData,
}: FieldValueContext): string => {
  const switchStatus = rowData[ROW_DATA_KEYS.AR_SWITCH_STATUS];
  const baseValue = formatFieldValueToString(fieldName, fieldValue);

  return switchStatus ? `${baseValue} - ${switchStatus}` : baseValue;
};

const getCashClearingGroupDisplayValue = ({
  fieldValue,
  iamUserState,
}: FieldValueContext): RowDataValueType =>
  fieldValue
    ? getSelectedGroupIdOption(
        fieldValue as string,
        iamUserState?.eisGroups?.groups ?? [],
      ).label
    : fieldValue;

const getCashClearingIsEnabledDisplayValue = ({
  fieldValue,
}: FieldValueContext): RowDataValueType => {
  return fieldValue != null
    ? fieldValue
      ? "Enabled"
      : "Disabled"
    : fieldValue;
};

const getFormIdOrProvisionIdDisplayValue = ({
  fieldName,
  fieldValue,
  rowData,
  evaluatedResults,
}: FieldValueContext): RowDataValueType => {
  return fieldValue
    ? getFormIdOrProvisionIdLabel(
        fieldName,
        fieldValue as string,
        evaluatedResults,
        rowData,
      )
    : fieldValue;
};

const getEnumDisplayValue = ({
  setupName,
  fieldName,
  fieldValue,
  schema,
}: FieldValueContext): RowDataValueType =>
  getLabelForEnumValue({
    setupKey: setupName,
    fieldName,
    fieldValue,
    property: schema.properties?.[fieldName],
  });

type DisplayValueConfig = {
  setup: string;
  fields: string[];
  getValue: (context: FieldValueContext) => RowDataValueType;
};

const DISPLAY_VALUE_CONFIGS: DisplayValueConfig[] = [
  {
    setup: SCHEMA_TITLES.ACCOUNTS_RECEIVABLE,
    fields: [ROW_DATA_KEYS.SCHEDULE_CODE],
    getValue: getAccountsReceivableScheduleCodeDisplayValue,
  },
  {
    setup: SCHEMA_TITLES.CASH_CLEARING_SETUP,
    fields: [
      ROW_DATA_KEYS.INTEGRATING_APPLICATION,
      ROW_DATA_KEYS.TRANSACTION_TYPE,
    ],
    getValue: getEnumDisplayValue,
  },
  {
    setup: SCHEMA_TITLES.CASH_CLEARING_SETUP,
    fields: [
      ROW_DATA_KEYS.CASH_CLEARING_COMPANY_GROUP_ID,
      ROW_DATA_KEYS.CASH_IN_BANK_COMPANY_GROUP_ID,
    ],
    getValue: getCashClearingGroupDisplayValue,
  },
  {
    setup: SCHEMA_TITLES.CASH_CLEARING_SETUP,
    fields: [ROW_DATA_KEYS.IS_ENABLED],
    getValue: getCashClearingIsEnabledDisplayValue,
  },
  {
    setup: SCHEMA_TITLES.REMITTANCE_FORM_MAPPING,
    fields: [ROW_DATA_KEYS.FORM_ID, ROW_DATA_KEYS.PROVISION_ID],
    getValue: getFormIdOrProvisionIdDisplayValue,
  },
  {
    setup: SCHEMA_TITLES.TRUCK_AP_INVOICES,
    fields: [ROW_DATA_KEYS.MANUFACTURER_CODE],
    getValue: getEnumDisplayValue,
  },
];

export const resolveFieldDisplayValue = (
  context: FieldValueContext,
): RowDataValueType => {
  const { fieldName, fieldValue, setupName } = context;

  const matchingField = DISPLAY_VALUE_CONFIGS.find(
    ({ setup, fields }) => setup === setupName && fields.includes(fieldName),
  );

  return matchingField ? matchingField.getValue(context) : fieldValue;
};
